import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { ISGLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of ISG Blockers.
 */
const BlockersDrawPanel: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      editOptions: {
        allLayers: [
          {
            ID: ISGLayerIds.blockers,
            GeometryType: "LineString",
            getFeatureProperties: () => ({ layerId: ISGLayerIds.blockers, Tier1: "true", Tier2: "false", Tier2_MST: "false", Tier3: "false" }),
            Attributes: {
              Tier1: { Type: "BOOLEAN" },
              Tier2: { Type: "BOOLEAN" },
              Tier2_MST: { Type: "BOOLEAN" },
              Tier3: { Type: "BOOLEAN" },
            },
            PromptAttributesOnCreate: true,
          },
        ],
        editingLayerId: ISGLayerIds.blockers, // Default selected layer
        editingLayerIds: [ISGLayerIds.blockers], // Default editing layers (enables edit mode)
      },
    }),
    []
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={defaultOptions} config={allToolConfig}>
      <MultiLayerEditPanelContent />
    </MultiLayerEditProvider>
  );
};

export default BlockersDrawPanel;
